<script lang="ts">
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import ActionBox from "@kontextwerk/web-sdk/ui/layout/ActionBox.svelte"
  import StatusTag from "@kontextwerk/web-sdk/ui/display/StatusTag.svelte"
  import UserProfileBubble from "@kontextwerk/web-sdk/ui/display/UserProfileBubble.svelte"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import { spaLink } from "@kontextwerk/web-sdk/core/routing/navigation"
  import UserTabState from "@kontextwerk/control-web-frame/shared/modules/user-tab-state"
  import { userDisplayName } from "@kontextwerk/control-web-frame/shared/logic/user-presentation"
  import {
    mdiAccountCircle,
    mdiCheckCircleOutline,
    mdiDevices,
    mdiLockOutline,
    mdiLockReset,
    mdiShieldCheckOutline,
  } from "@mdi/js"
  import type { UserViewFrameContext } from "../../../../_shared/_logic/user-types"

  const { viewFrameContext } = $props<{ viewFrameContext: UserViewFrameContext }>()

  // Profile, credentials, MFA and devices belong to the personal identity (KW-SEC-AUT-014):
  // another member's record is read-only, the own record links to the personal pages.
  const ACCOUNT_LINKS = [
    { href: "/account/profile", label: "Mein Profil", icon: mdiAccountCircle },
    { href: "/account/password", label: "Passwort", icon: mdiLockReset },
    { href: "/account/mfa", label: "Sicherheit", icon: mdiShieldCheckOutline },
    { href: "/account/devices", label: "Meine Geräte", icon: mdiDevices },
  ] as const

  const user = $derived(viewFrameContext.user)
  const access = $derived(viewFrameContext.memberAccess)
  const email = $derived(access?.record?.email ?? (user?.email?.trim() || null))
  const status = $derived(
    access?.record?.status ?? viewFrameContext.member?.membershipStatus ?? null,
  )
  const avatar = $derived({
    displayName: userDisplayName(user),
    firstName: user?.first_name ?? null,
    lastName: user?.last_name ?? null,
    email,
    avatarUrl: user?.profile_image_url ?? viewFrameContext.member?.profileImageUrl ?? null,
  })
</script>

{#if viewFrameContext.loading || viewFrameContext.error || !user}
  <UserTabState
    loading={viewFrameContext.loading}
    error={viewFrameContext.error}
    userPresent={Boolean(user)}
    onRetry={viewFrameContext.onRetry ?? null}
  />
{:else}
  <div class="form">
    <div class="profile-grid">
      <Input placeholder="Username" value={user.username ?? ""} disabled={true} />
      {#if email}
        <Input placeholder="E-Mail" value={email} disabled={true} />
      {/if}
      <Input placeholder="Vorname" value={user.first_name ?? ""} disabled={true} />
      <Input placeholder="Nachname" value={user.last_name ?? ""} disabled={true} />
    </div>

    <ActionBox label="Profilfoto">
      <div class="photo">
        <UserProfileBubble user={avatar} size={72} />
      </div>
    </ActionBox>

    {#if status}
      <ActionBox label="Status">
        <div class="status">
          {#if status === "active"}
            <StatusTag label="Aktiv" color="emerald" icon={mdiCheckCircleOutline} />
          {:else}
            <StatusTag label="Gesperrt" color="rose" icon={mdiLockOutline} />
          {/if}
        </div>
      </ActionBox>
    {/if}

    {#if viewFrameContext.isOwnAccount}
      <ActionBox label="Mein Konto">
        <ul class="account-links">
          {#each ACCOUNT_LINKS as link (link.href)}
            <li>
              <a href={link.href} use:spaLink>
                <Icon path={link.icon} size="1.1rem" />
                <span>{link.label}</span>
              </a>
            </li>
          {/each}
        </ul>
      </ActionBox>
    {/if}
  </div>
{/if}

<style lang="less">
  .form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .profile-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
  }

  .photo,
  .status {
    display: flex;
    align-items: center;
    padding: 0.4rem 0;
  }

  .account-links {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;

    a {
      display: inline-flex;
      align-items: center;
      gap: 0.6rem;
      padding: 0.4rem 0;
      color: var(--text-invers-100);
      text-decoration: none;
      overflow-wrap: anywhere;

      &:hover {
        color: var(--primary-100);
      }

      &:focus-visible {
        outline: 2px solid var(--primary-100);
        outline-offset: 2px;
      }
    }
  }

  @media (max-width: 720px) {
    .profile-grid {
      grid-template-columns: 1fr;
    }
  }
</style>
